import React, { useState, useRef, useEffect } from 'react';

const ChatInterface = ({
  clientId,
  messages,
  loading,
  error,
  onSendMessage,
  onSourceClick
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showTimestampFilter, setShowTimestampFilter] = useState(false);
  const [timestampFrom, setTimestampFrom] = useState('');
  const [timestampTo, setTimestampTo] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !loading && clientId) {
      // Pass timestamp filters along with the message
      const timestampFilters = {};
      if (timestampFrom) {
        timestampFilters.timestampFrom = new Date(timestampFrom).toISOString();
      }
      if (timestampTo) {
        timestampFilters.timestampTo = new Date(timestampTo).toISOString();
      }

      onSendMessage(inputValue.trim(), timestampFilters);
      setInputValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  const formatProcessingTime = (timeMs) => {
    if (!timeMs) return '';
    if (timeMs < 1000) return `${Math.round(timeMs)}ms`;
    return `${(timeMs / 1000).toFixed(1)}s`;
  };

  const formatConfidence = (confidence) => {
    if (!confidence) return '';
    // Handle numeric confidence (0.0-1.0)
    if (typeof confidence === 'number') {
      return `${Math.round(confidence * 100)}%`;
    }
    // Handle legacy string values (HIGH, MEDIUM, LOW)
    return confidence;
  };

  const handleSourceClick = (sourceDoc) => {
    if (onSourceClick) {
      onSourceClick(sourceDoc);
    }
  };

  // Group source documents by document name and aggregate scores
  const groupSourcesByDocument = (sources) => {
    if (!sources || sources.length === 0) return [];

    const grouped = sources.reduce((acc, source) => {
      const docName = source.document_name;
      if (!acc[docName]) {
        acc[docName] = {
          document_name: docName,
          document_id: source.document_id,
          chunks: [],
          avgScore: 0,
          maxScore: 0,
          pages: new Set(),
        };
      }

      acc[docName].chunks.push(source);
      acc[docName].maxScore = Math.max(acc[docName].maxScore, source.score || 0);
      if (source.page_number) {
        acc[docName].pages.add(source.page_number);
      }

      return acc;
    }, {});

    // Calculate average scores and convert to array
    return Object.values(grouped).map(doc => {
      doc.avgScore = doc.chunks.reduce((sum, c) => sum + (c.score || 0), 0) / doc.chunks.length;
      doc.pagesList = Array.from(doc.pages).sort((a, b) => a - b);
      return doc;
    }).sort((a, b) => b.maxScore - a.maxScore); // Sort by highest score
  };

  // Process message content to handle source citations and newlines
  const processCitations = (content) => {
    if (!content) return content;

    // First, handle newlines by splitting content
    const lines = content.split(/\\n|\n/);
    const allParts = [];

    lines.forEach((line, lineIndex) => {
      // Process citations within each line
      const parts = [];
      let lastIndex = 0;
      // Updated regex to capture data attributes
      const regex = /<source_citation data-text='([^']+)' data-source='([^']+)'>([^<]+)<\/source_citation>/g;
      let match;
      let citationIndex = 0;

      while ((match = regex.exec(line)) !== null) {
        // Add text before citation
        if (match.index > lastIndex) {
          parts.push(line.substring(lastIndex, match.index));
        }

        const citedText = match[1];      // The actual value being cited (e.g., "6/24")
        const sourceContext = match[2];   // The source context from document
        const displayText = match[3];     // What's shown in answer (should match citedText)

        parts.push(
          <span
            key={`line-${lineIndex}-citation-${citationIndex++}`}
            className="source-citation"
            style={{
              color: '#667eea',
              cursor: 'pointer',
              borderBottom: '1px dotted #667eea',
              padding: '0 2px',
              position: 'relative',
              fontWeight: '600'
            }}
            onMouseEnter={(e) => {
              const tooltip = e.currentTarget.querySelector('.citation-tooltip');
              if (tooltip) tooltip.style.display = 'block';
            }}
            onMouseLeave={(e) => {
              // Only hide if we're not moving to the tooltip
              const tooltip = e.currentTarget.querySelector('.citation-tooltip');
              if (tooltip && !e.relatedTarget?.closest('.citation-tooltip')) {
                setTimeout(() => {
                  if (!tooltip.matches(':hover')) {
                    tooltip.style.display = 'none';
                  }
                }, 100);
              }
            }}
          >
            {displayText}
            <span
              className="citation-tooltip"
              style={{
                display: 'none',
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                padding: '12px 16px',
                background: 'rgba(0, 0, 0, 0.9)',
                color: 'white',
                fontSize: '0.85rem',
                borderRadius: '6px',
                whiteSpace: 'pre-wrap',
                maxWidth: '600px',
                minWidth: '400px',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 10000,
                pointerEvents: 'auto',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                lineHeight: '1.5',
                border: '2px solid #667eea'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.currentTarget.parentElement.style.display = 'none';
                }}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s',
                  padding: 0,
                  lineHeight: 1
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ×
              </button>
              <div style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                color: '#4ade80',
                borderBottom: '1px solid rgba(255,255,255,0.2)',
                paddingBottom: '6px',
                paddingRight: '30px'
              }}>
                Source Citation
              </div>
              <div style={{ textAlign: 'left', wordBreak: 'break-word' }}>
                ...{sourceContext}...
              </div>
            </span>
          </span>
        );

        lastIndex = regex.lastIndex;
      }

      // Add remaining text in line
      if (lastIndex < line.length) {
        parts.push(line.substring(lastIndex));
      }

      // Add processed line to all parts
      if (parts.length > 0) {
        allParts.push(
          <span key={`line-${lineIndex}`}>
            {parts}
          </span>
        );
      }

      // Add line break after each line except the last
      if (lineIndex < lines.length - 1) {
        allParts.push(<br key={`br-${lineIndex}`} />);
      }
    });

    return allParts.length > 0 ? allParts : content;
  };

  const sampleQuestions = [
    "What medications is the patient taking?",
    "What are the patient's vital signs?",
    "What is the primary diagnosis?",
    "What allergies does the patient have?",
    "When was the patient admitted?",
    "What procedures were performed?",
  ];

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <h3>Ask Questions</h3>
        {clientId ? (
          <div className="chat-subtitle">
            Ask questions about {clientId}'s documents
          </div>
        ) : (
          <div className="chat-subtitle">
            Select a client to start asking questions
          </div>
        )}
      </div>

      <div className="chat-messages">
        {messages.length === 0 && clientId && (
          <div className="empty-state">
            <div style={{ marginBottom: '1rem' }}>
              Start a conversation by asking a question about the uploaded documents.
            </div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              <strong>Try asking:</strong>
              <ul style={{ textAlign: 'left', marginTop: '0.5rem' }}>
                {sampleQuestions.slice(0, 3).map((question, index) => (
                  <li 
                    key={index}
                    style={{ 
                      cursor: 'pointer', 
                      color: '#667eea',
                      margin: '0.25rem 0'
                    }}
                    onClick={() => setInputValue(question)}
                  >
                    "{question}"
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type}`}>
            <div className="message-bubble">
              {processCitations(message.content)}
            </div>
            
            <div className="message-time">
              {message.type === 'user' ? (
                <span>You</span>
              ) : (
                <span>
                  Assistant
                  {message.confidence && (
                    <span style={{ marginLeft: '0.5rem', color: '#999' }}>
                      • {formatConfidence(message.confidence)} confidence
                    </span>
                  )}
                  {message.processingTime && (
                    <span style={{ marginLeft: '0.5rem', color: '#999' }}>
                      • {formatProcessingTime(message.processingTime)}
                    </span>
                  )}
                </span>
              )}
              <span style={{ marginLeft: '0.5rem', color: '#999' }}>
                {message.timestamp.toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </span>
            </div>

            {message.sourceDocuments && message.sourceDocuments.length > 0 && (
              <div className="message-sources" style={{ marginTop: '0.75rem' }}>
                <div style={{
                  fontSize: '0.7rem',
                  color: '#999',
                  marginBottom: '0.4rem',
                  fontWeight: '500'
                }}>
                  {groupSourcesByDocument(message.sourceDocuments).length} source{groupSourcesByDocument(message.sourceDocuments).length > 1 ? 's' : ''} • {message.sourceDocuments.length} chunk{message.sourceDocuments.length > 1 ? 's' : ''}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {groupSourcesByDocument(message.sourceDocuments).map((docGroup, index) => (
                    <div
                      key={index}
                      className="source-chip-group"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.4rem 0.5rem',
                        background: '#f8f9fa',
                        borderRadius: '4px',
                        border: '1px solid #e1e4e8',
                        fontSize: '0.75rem',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1', minWidth: '200px' }}>
                        <span
                          onClick={() => handleSourceClick(docGroup.chunks[0])}
                          style={{
                            fontWeight: '600',
                            color: '#667eea',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '180px'
                          }}
                          title={docGroup.document_name}
                        >
                          📄 {docGroup.document_name}
                        </span>
                        <span style={{
                          fontSize: '0.65rem',
                          padding: '0.1rem 0.3rem',
                          background: docGroup.maxScore > 0.5 ? '#d4edda' : docGroup.maxScore > 0.4 ? '#fff3cd' : '#f8d7da',
                          color: docGroup.maxScore > 0.5 ? '#155724' : docGroup.maxScore > 0.4 ? '#856404' : '#721c24',
                          borderRadius: '3px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}>
                          {Math.round(docGroup.maxScore * 100)}%
                        </span>
                      </div>
                      {docGroup.pagesList.length > 0 && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          flexWrap: 'wrap'
                        }}>
                          <span style={{ fontSize: '0.65rem', color: '#999' }}>p.</span>
                          {docGroup.pagesList.map((pageNum, pageIdx) => {
                            // Get ALL chunks for this page (not just the first one)
                            const chunksOnPage = docGroup.chunks.filter(c => c.page_number === pageNum);
                            const maxScore = Math.max(...chunksOnPage.map(c => c.score || 0));
                            const avgScore = chunksOnPage.reduce((sum, c) => sum + (c.score || 0), 0) / chunksOnPage.length;
                            const displayScore = Math.round(maxScore * 100);

                            return (
                              <span
                                key={pageIdx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Click opens the highest scoring chunk on this page
                                  const bestChunk = chunksOnPage.reduce((best, current) =>
                                    (current.score || 0) > (best.score || 0) ? current : best
                                  );
                                  if (bestChunk) {
                                    handleSourceClick(bestChunk);
                                  }
                                }}
                                style={{
                                  position: 'relative',
                                  padding: '0.1rem 0.35rem',
                                  background: '#667eea',
                                  color: 'white',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  fontSize: '0.65rem',
                                  transition: 'background 0.2s',
                                  whiteSpace: 'nowrap'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#5568d3';
                                  // Show tooltip
                                  const tooltip = e.currentTarget.querySelector('.custom-tooltip');
                                  if (tooltip) tooltip.style.display = 'block';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#667eea';
                                  // Only hide tooltip if we're not moving to it
                                  const tooltip = e.currentTarget.querySelector('.custom-tooltip');
                                  if (tooltip && !e.relatedTarget?.closest('.custom-tooltip')) {
                                    setTimeout(() => {
                                      if (!tooltip.matches(':hover')) {
                                        tooltip.style.display = 'none';
                                      }
                                    }, 100);
                                  }
                                }}
                              >
                                {pageNum}
                                {chunksOnPage.length > 1 && (
                                  <span style={{
                                    fontSize: '0.55rem',
                                    marginLeft: '1px',
                                    opacity: 0.8
                                  }}>
                                    ×{chunksOnPage.length}
                                  </span>
                                )}
                                <span
                                  className="custom-tooltip"
                                  style={{
                                    display: 'none',
                                    position: 'fixed',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    padding: '16px 20px',
                                    background: 'rgba(0, 0, 0, 0.95)',
                                    color: 'white',
                                    fontSize: '0.85rem',
                                    borderRadius: '8px',
                                    whiteSpace: 'pre-wrap',
                                    maxWidth: '800px',
                                    minWidth: '500px',
                                    maxHeight: '600px',
                                    overflowY: 'auto',
                                    zIndex: 10000,
                                    pointerEvents: 'auto',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                                    lineHeight: '1.6',
                                    border: '2px solid #667eea'
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                >
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.currentTarget.parentElement.style.display = 'none';
                                    }}
                                    style={{
                                      position: 'absolute',
                                      top: '12px',
                                      right: '12px',
                                      background: 'rgba(255, 255, 255, 0.2)',
                                      border: 'none',
                                      color: 'white',
                                      width: '28px',
                                      height: '28px',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '18px',
                                      fontWeight: 'bold',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'background 0.2s',
                                      padding: 0,
                                      lineHeight: 1
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                                  >
                                    ×
                                  </button>
                                  <div style={{
                                    fontWeight: 'bold',
                                    marginBottom: '12px',
                                    color: '#4ade80',
                                    fontSize: '1rem',
                                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                                    paddingBottom: '8px',
                                    paddingRight: '40px'
                                  }}>
                                    Page {pageNum} • {chunksOnPage.length} chunk{chunksOnPage.length > 1 ? 's' : ''} • {displayScore}% max match
                                  </div>
                                  {chunksOnPage
                                    .sort((a, b) => (b.score || 0) - (a.score || 0))
                                    .map((chunk, chunkIdx) => (
                                      <div
                                        key={chunkIdx}
                                        style={{
                                          marginBottom: chunkIdx < chunksOnPage.length - 1 ? '16px' : '0',
                                          paddingBottom: chunkIdx < chunksOnPage.length - 1 ? '16px' : '0',
                                          borderBottom: chunkIdx < chunksOnPage.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none'
                                        }}
                                      >
                                        <div style={{
                                          fontSize: '0.75rem',
                                          marginBottom: '6px',
                                          color: '#fbbf24',
                                          fontWeight: 'bold'
                                        }}>
                                          Chunk {chunkIdx + 1} • {Math.round((chunk.score || 0) * 100)}% match
                                        </div>
                                        <div style={{
                                          textAlign: 'left',
                                          wordBreak: 'break-word'
                                        }}>
                                          ...{chunk.content.replace(/\n/g, ' ').trim()}...
                                        </div>
                                      </div>
                                    ))}
                                </span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {message.isError && (
              <div style={{ 
                marginTop: '0.5rem', 
                padding: '0.5rem', 
                background: '#ffe6e6', 
                borderRadius: '4px',
                fontSize: '0.8rem',
                color: '#d63031'
              }}>
                ⚠️ This message indicates an error occurred
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="message assistant">
            <div className="message-bubble">
              <div className="chat-loading">
                <span>Thinking</span>
                <div className="loading-dots">
                  <span>●</span>
                  <span>●</span>
                  <span>●</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        {/* Timestamp Filter Toggle */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem',
          padding: '0.5rem',
          background: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #e1e4e8'
        }}>
          <button
            type="button"
            onClick={() => setShowTimestampFilter(!showTimestampFilter)}
            disabled={!clientId}
            style={{
              background: showTimestampFilter ? '#667eea' : 'transparent',
              color: showTimestampFilter ? 'white' : '#667eea',
              border: '1px solid #667eea',
              padding: '0.4rem 0.8rem',
              borderRadius: '4px',
              cursor: !clientId ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem',
              fontWeight: '600',
              opacity: !clientId ? 0.5 : 1,
              transition: 'all 0.2s'
            }}
          >
            🕐 {showTimestampFilter ? 'Hide' : 'Show'} Time Filter
          </button>
          {(timestampFrom || timestampTo) && (
            <div style={{ fontSize: '0.7rem', color: '#667eea', fontWeight: '600' }}>
              {timestampFrom && `From: ${new Date(timestampFrom).toLocaleDateString()}`}
              {timestampFrom && timestampTo && ' • '}
              {timestampTo && `To: ${new Date(timestampTo).toLocaleDateString()}`}
            </div>
          )}
        </div>

        {/* Timestamp Filter Inputs */}
        {showTimestampFilter && (
          <div style={{
            padding: '0.75rem',
            background: '#f0f4ff',
            borderRadius: '4px',
            border: '1px solid #d0d9ff',
            marginBottom: '0.75rem'
          }}>
            <div style={{
              fontSize: '0.75rem',
              color: '#667eea',
              marginBottom: '0.5rem',
              fontWeight: '600'
            }}>
              Filter by Time Range
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.75rem'
            }}>
              <div>
                <label style={{
                  fontSize: '0.7rem',
                  color: '#666',
                  display: 'block',
                  marginBottom: '0.25rem'
                }}>
                  From
                </label>
                <input
                  type="datetime-local"
                  value={timestampFrom}
                  onChange={(e) => setTimestampFrom(e.target.value)}
                  disabled={!clientId || loading}
                  style={{
                    width: '100%',
                    padding: '0.4rem',
                    borderRadius: '4px',
                    border: '1px solid #d0d9ff',
                    fontSize: '0.75rem'
                  }}
                />
              </div>
              <div>
                <label style={{
                  fontSize: '0.7rem',
                  color: '#666',
                  display: 'block',
                  marginBottom: '0.25rem'
                }}>
                  To
                </label>
                <input
                  type="datetime-local"
                  value={timestampTo}
                  onChange={(e) => setTimestampTo(e.target.value)}
                  disabled={!clientId || loading}
                  style={{
                    width: '100%',
                    padding: '0.4rem',
                    borderRadius: '4px',
                    border: '1px solid #d0d9ff',
                    fontSize: '0.75rem'
                  }}
                />
              </div>
            </div>
            <div style={{
              marginTop: '0.5rem',
              display: 'flex',
              gap: '0.5rem'
            }}>
              <button
                type="button"
                onClick={() => {
                  setTimestampFrom('');
                  setTimestampTo('');
                }}
                style={{
                  padding: '0.3rem 0.6rem',
                  fontSize: '0.7rem',
                  background: 'white',
                  border: '1px solid #d0d9ff',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  color: '#667eea'
                }}
              >
                Clear Filters
              </button>
              <div style={{
                fontSize: '0.65rem',
                color: '#999',
                padding: '0.3rem 0',
                display: 'flex',
                alignItems: 'center'
              }}>
                💡 Leave empty to search all dates
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="chat-input-container">
            <textarea
              ref={textareaRef}
              className="chat-input"
              placeholder={
                !clientId
                  ? "Select a client first..."
                  : "Ask a question about the documents..."
              }
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={!clientId || loading}
              rows={1}
            />

            <button
              type="submit"
              className="chat-send-btn"
              disabled={!inputValue.trim() || !clientId || loading}
            >
              {loading ? (
                <span>●●●</span>
              ) : (
                <>
                  <span>Send</span>
                  <span>→</span>
                </>
              )}
            </button>
          </div>
        </form>

        {error && (
          <div className="chat-error">
            {error}
          </div>
        )}

        {!clientId && (
          <div style={{ 
            fontSize: '0.8rem', 
            color: '#999', 
            marginTop: '0.5rem',
            textAlign: 'center'
          }}>
            Select a client and upload documents to start asking questions
          </div>
        )}
      </div>

    </div>
  );
};

export default ChatInterface;